import React from "react";
import styled from "styled-components";
import { RichText as PrismicRichText } from "prismic-reactjs";
import AriaTabPanel from "react-aria-tabpanel";

import { columnPush, linkResolver, serializer } from "utils";
import { H2, H3, Image, RichText } from "components";

const Content = ({ body, heading, src, fluid, alt }) => (
  <div
    css={`
      padding: 2.5vw 0;
    `}
  >
    <H2 children={heading} mb="0.5em" color="brand.nickel" />
    <RichText children={body} mb="2em" />
    <div
      css={`
        position: relative;
      `}
    >
      <Image src={src} fluid={fluid} alt={alt} />
    </div>
  </div>
);

const TabList = styled.ul`
  display: flex;
  flex-direction: column;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const TabListItem = styled.li`
  margin: 0;
  > div:focus {
    outline: 0;
  }
`;

function TabButton(content, tabState) {
  return (
    <div
      className={tabState.isActive ? "isActive" : ""}
      css={`
        position: relative;
        margin: 1em 0;
        ${columnPush(1)};
        color: ${(props) => props.theme.colors.text.default};
        opacity: 0.5;
        cursor: pointer;
        &::before {
          top: 0.75em !important;
          opacity: 0;
        }
        &.isActive {
          opacity: 1;
          &::before {
            opacity: 1;
          }
        }
      `}
    >
      {content}
    </div>
  );
}

const Tabs = ({ data, page, preview }) => (
  <>
    {page.features && (
      <AriaTabPanel.Wrapper
        letterNavigation={true}
        css={`
          display: -ms-grid;
          display: grid;
          -ms-grid-columns: 1fr 10.75vw 1fr;
          grid-template-columns: 1fr 10.75vw 1fr;
        `}
      >
        <AriaTabPanel.TabList
          css={`
            -ms-grid-column: 1;
            grid-column: 1;
            -ms-grid-row: 1;
            grid-row: 1;
            padding: 2.5vw 0;
          `}
        >
          <TabList>
            {page.features.map((feature, index) => (
              <TabListItem>
                <AriaTabPanel.Tab
                  id={"tab" + index}
                  letterNavigationText={feature.feature}
                >
                  {TabButton.bind(
                    null,
                    <H3 children={feature.feature} color="inherit" />
                  )}
                </AriaTabPanel.Tab>
              </TabListItem>
            ))}
          </TabList>
        </AriaTabPanel.TabList>
        <div
          css={`
            -ms-grid-column: 2;
            grid-column: 2;
            -ms-grid-row: 1;
            grid-row: 1;
            display: flex;
            justify-content: center;
          `}
        >
          <div
            css={`
              width: 1px;
              height: 100%;
              background-color: ${(props) => props.theme.colors.bg.reverse};
            `}
          />
        </div>
        <div
          css={`
            -ms-grid-column: 3;
            grid-column: 3;
            -ms-grid-row: 1;
            grid-row: 1;
          `}
        >
          {page.features.map((feature, index) => (
            <AriaTabPanel.TabPanel tabId={"tab" + index}>
              <Content
                heading={
                  feature.feature_title &&
                  PrismicRichText.asText(feature.feature_title)
                }
                body={
                  feature.feature_description &&
                  PrismicRichText.render(
                    feature.feature_description,
                    linkResolver,
                    serializer
                  )
                }
                src={
                  preview
                    ? page.features[index].feature_image &&
                      page.features[index].feature_image.url
                    : undefined
                }
                fluid={
                  preview
                    ? undefined
                    : data.prismicAmenities.data.features[index]
                        .feature_image &&
                      data.prismicAmenities.data.features[index].feature_image
                        .localFile &&
                      data.prismicAmenities.data.features[index].feature_image
                        .localFile.childImageSharp.fluid
                }
                alt={
                  page.features[index].feature_image &&
                  page.features[index].feature_image.alt
                }
              />
            </AriaTabPanel.TabPanel>
          ))}
        </div>
      </AriaTabPanel.Wrapper>
    )}
  </>
);

const Switcher = (props) => <Tabs {...props} />;

export default Switcher;
